import type { ThemePaths } from '../types';

import { useThemeContext } from './useThemeContext';
import useThemeName from './useThemeName';

const cache = {} as { [key: string]: string };

const getThemeValueFromPath = <T extends string>({
  cacheKey,
  keys,
  themeOrNested,
}: {
  cacheKey?: string;
  keys: string[];
  themeOrNested: { [key: string]: { [key: string]: T } | T };
}): string => {
  if (cacheKey && cache[cacheKey]) return cache[cacheKey];

  const [first, ...rest] = keys;
  if (!first) return '';

  if (rest.length === 0) {
    const themeValue = (themeOrNested as { [key: string]: T })[first];
    if (cacheKey) cache[cacheKey] = themeValue;

    return themeValue;
  }

  return getThemeValueFromPath({
    themeOrNested: themeOrNested[first] as { [key: string]: T },
    keys: rest,
    cacheKey,
  });
};

/**
 * Usage
 * const themeValue = useThemeValue('icon.primary');
 */

const useThemeValue = (themePath: ThemePaths) => {
  const themeName = useThemeName();
  const { themes } = useThemeContext();

  const keys = themePath.split('.');
  const cacheKey = `${themeName}-${themePath}`;
  const theme = themes[themeName];

  if (!theme) {
    throw new Error(`Theme not defined: ${themeName}`);
  }

  return getThemeValueFromPath({ cacheKey, keys, themeOrNested: theme });
};

export default useThemeValue;
